import { useEffect, useState } from "react";
import { Loader2, Save, Sparkles, User as UserIcon, HeartPulse, Wallet, X, Plus } from "lucide-react";
import { toast } from "sonner";

import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const SMOKER = ["Non-smoker", "Occasional", "Smoker"];
const INCOME = ["< ₹5L", "₹5L – ₹10L", "₹10L – ₹25L", "₹25L – ₹50L", "₹50L – ₹1Cr", "> ₹1Cr"];
const TAX_PRIORITY = ["High", "Medium", "Low", "Not a priority"];

const COMMON_CONDITIONS = [
  "Diabetes", "Hypertension", "Cardiovascular", "Asthma", "Thyroid",
  "PCOS", "Cancer history", "Mental health", "None",
];

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    age: "",
    gender: "",
    family_size: "",
    city: "",
    pre_existing_conditions: [],
    smoker: "",
    income_bracket: "",
    tax_priority: "",
  });
  const [conditionInput, setConditionInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setProfile({
          age: data.age ?? "",
          gender: data.gender ?? "",
          family_size: data.family_size ?? "",
          city: data.city ?? "",
          pre_existing_conditions: data.pre_existing_conditions ?? [],
          smoker: data.smoker ?? "",
          income_bracket: data.income_bracket ?? "",
          tax_priority: data.tax_priority ?? "",
        });
      } catch (_e) { /* empty profile is OK */ }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k) => (v) => setProfile((p) => ({ ...p, [k]: v }));

  const addCondition = (c) => {
    const cleaned = c.trim();
    if (!cleaned) return;
    if (profile.pre_existing_conditions.includes(cleaned)) return;
    setProfile((p) => ({ ...p, pre_existing_conditions: [...p.pre_existing_conditions, cleaned] }));
    setConditionInput("");
  };

  const removeCondition = (c) => {
    setProfile((p) => ({ ...p, pre_existing_conditions: p.pre_existing_conditions.filter((x) => x !== c) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profile,
        age: profile.age === "" ? null : Number(profile.age),
        family_size: profile.family_size === "" ? null : Number(profile.family_size),
        gender: profile.gender || null,
        city: profile.city || null,
        smoker: profile.smoker || null,
        income_bracket: profile.income_bracket || null,
        tax_priority: profile.tax_priority || null,
      };
      await api.put("/profile", payload);
      toast.success("Profile updated. InsureGenie will now personalize answers.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Profile" eyebrow="Preferences">
        <div className="h-full flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Your profile"
      eyebrow="Preferences"
      action={
        <Button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-9"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
        </Button>
      }
    >
      <div data-testid="profile-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-10 space-y-10">
        <div className="flex items-start gap-3 bg-accent/5 border border-accent/20 rounded-2xl p-4">
          <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong className="text-foreground">Personalize every conversation.</strong>{" "}
            Anything you share here is used silently in the background to tailor InsureGenie&apos;s
            answers. You can leave fields blank or edit them at any time.
          </div>
        </div>

        {/* Basic */}
        <Section icon={UserIcon} title="Basic">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Age">
              <Input data-testid="profile-age" type="number" min="0" max="120" value={profile.age} onChange={(e) => set("age")(e.target.value)} placeholder="e.g. 32" className="h-11 rounded-xl" />
            </Field>
            <Field label="Gender">
              <Select value={profile.gender} onValueChange={set("gender")}>
                <SelectTrigger data-testid="profile-gender" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Family size">
              <Input data-testid="profile-family-size" type="number" min="1" max="20" value={profile.family_size} onChange={(e) => set("family_size")(e.target.value)} placeholder="e.g. 4" className="h-11 rounded-xl" />
            </Field>
            <Field label="City">
              <Input data-testid="profile-city" value={profile.city} onChange={(e) => set("city")(e.target.value)} placeholder="e.g. Mumbai" className="h-11 rounded-xl" />
            </Field>
          </div>
        </Section>

        {/* Health */}
        <Section icon={HeartPulse} title="Health">
          <Field label="Pre-existing conditions">
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {profile.pre_existing_conditions.map((c) => (
                  <Badge key={c} data-testid={`profile-condition-${c}`} variant="secondary" className="rounded-full px-3 py-1 text-xs gap-1.5 bg-accent/10 text-foreground hover:bg-accent/20 border border-accent/20">
                    {c}
                    <button onClick={() => removeCondition(c)} className="hover:text-destructive" aria-label="remove">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {profile.pre_existing_conditions.length === 0 && (
                  <span className="text-xs text-muted-foreground italic">None added</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="profile-condition-input"
                  value={conditionInput}
                  onChange={(e) => setConditionInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCondition(conditionInput); } }}
                  placeholder="Type a condition and press Enter"
                  className="h-10 rounded-xl"
                />
                <Button data-testid="profile-condition-add-btn" onClick={() => addCondition(conditionInput)} variant="outline" size="icon" className="rounded-xl h-10 w-10 flex-shrink-0"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COMMON_CONDITIONS.filter((c) => !profile.pre_existing_conditions.includes(c)).map((c) => (
                  <button
                    key={c}
                    data-testid={`profile-condition-suggest-${c}`}
                    onClick={() => addCondition(c)}
                    className="text-[11px] text-muted-foreground border border-border rounded-full px-2.5 py-1 hover:border-accent hover:text-foreground transition-colors"
                  >
                    + {c}
                  </button>
                ))}
              </div>
            </div>
          </Field>
          <Field label="Smoker status">
            <Select value={profile.smoker} onValueChange={set("smoker")}>
              <SelectTrigger data-testid="profile-smoker" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SMOKER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </Section>

        {/* Financial */}
        <Section icon={Wallet} title="Financial">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Income bracket (annual)">
              <Select value={profile.income_bracket} onValueChange={set("income_bracket")}>
                <SelectTrigger data-testid="profile-income" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INCOME.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Tax-saving priority">
              <Select value={profile.tax_priority} onValueChange={set("tax_priority")}>
                <SelectTrigger data-testid="profile-tax-priority" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{TAX_PRIORITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </Section>
      </div>
    </AppShell>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
